<template>
    <v-container fluid>
        <v-alert
            v-if="pendingItems.length > 0"
            class="pending-alert mb-3"
            dense
            icon="mdi-alert"
        >
            <div class="d-flex align-center flex-grow-1">
                <span class="pending-alert__text">
                    There are
                    <strong>{{ pendingItems.length }}</strong> items pending
                    assignment to a scope.
                </span>
                <v-spacer />
                <v-btn
                    text
                    class="pending-alert__action"
                    @click="openReviewDialog"
                >
                    Review
                </v-btn>
            </div>
        </v-alert>

        <v-data-table
            :headers="headers"
            :items="tableRows"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            disable-pagination
            hide-default-footer
            fixed-header
        >
            <template v-slot:top>
                <div class="milestones-table-top">
                    <v-row no-gutters class="mt-0 mx-0 pt-4 pb-0">
                        <div class="d-flex flex-wrap">
                            <v-card
                                v-for="card in insightCards"
                                :key="card.title"
                                class="pt-3 pl-2 pr-5 mx-3 mb-2"
                                min-width="180px"
                                height="75px"
                            >
                                <v-card-text
                                    class="d-flex my-0 py-0 mx-0 px-0"
                                    width="100%"
                                >
                                    <v-chip
                                        class="mb-1 mt-0 mx-2 pt-0 white--text"
                                        :color="card.color"
                                        label
                                        :style="{ height: '50px' }"
                                    >
                                        <v-icon center>
                                            {{ card.icon }}
                                        </v-icon>
                                    </v-chip>
                                    <div class="mb-1 mt-0 pt-0">
                                        <div
                                            class="d-flex mr-0 pr-0 text-capitalize"
                                        >
                                            <span
                                                :style="{
                                                    'font-size': '14px',
                                                    'font-weight': 'bold',
                                                }"
                                                >{{ card.title }}</span
                                            >
                                        </div>
                                        <p class="text-h6 black--text">
                                            {{ card.value }}
                                        </p>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </div>
                    </v-row>
                    <v-row
                        no-gutters
                        class="mt-0 mx-0 pt-2 pb-4 px-3 align-center"
                    >
                        <v-btn color="primary" rounded @click="openCreateScope">
                            <v-icon left>mdi-plus</v-icon>
                            Create scope Group
                        </v-btn>
                        <v-spacer />

                        <v-btn-toggle
                            v-model="expandMode"
                            dense
                            bordered
                            color="primary"
                            :disabled="scopeGroups.length === 0"
                        >
                            <v-btn small value="expand" @click="expandAll">
                                Expand all
                            </v-btn>
                            <v-btn small value="collapse" @click="collapseAll">
                                Collapse all
                            </v-btn>
                        </v-btn-toggle>
                    </v-row>
                </div>
            </template>

            <template v-slot:[`header.milestone`]="{ header }">
                <div class="text-center">
                    <div>{{ header.text }}</div>
                    <div
                        v-if="
                            header.weight !== undefined &&
                                header.weight !== null
                        "
                        class="caption grey--text"
                    >
                        ({{ formatWeight(header.weight) }}%)
                    </div>
                </div>
            </template>

            <template v-slot:body>
                <tbody v-if="!loading && scopeGroups.length === 0">
                    <tr>
                        <td :colspan="headers.length || 1" class="pa-0">
                            <div
                                class="empty-state d-flex flex-column align-center justify-center py-12"
                            >
                                <h3 class="mb-2">
                                    No scope groups created yet.
                                </h3>
                                <p
                                    class="grey--text text-center mb-4"
                                    style="max-width: 420px"
                                >
                                    Milestones starts empty. Create a scope
                                    group to choose which items you want to
                                    track.
                                </p>
                            </div>
                        </td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr
                        v-for="entry in displayRows"
                        :key="entry.key"
                        :class="
                            entry.kind === 'group'
                                ? 'group-row'
                                : 'group-member-row'
                        "
                    >
                        <template v-if="entry.kind === 'group'">
                            <td class="text-center">
                                {{ entry.row.quoteNumbers }}
                            </td>
                            <td>
                                <div class="d-flex align-center">
                                    <v-btn
                                        small
                                        icon
                                        class="mr-1"
                                        @click="toggleExpand(entry.row.id)"
                                    >
                                        <v-icon>
                                            {{
                                                expandedGroups[entry.row.id]
                                                    ? 'mdi-chevron-down'
                                                    : 'mdi-chevron-right'
                                            }}
                                        </v-icon>
                                    </v-btn>
                                    <span class="font-weight-medium">{{
                                        entry.row.name
                                    }}</span>
                                </div>
                            </td>
                            <td>
                                <span class="font-weight-medium">
                                    {{ entry.row.productTypeName }}
                                </span>
                            </td>
                            <td class="text-right">
                                {{ formatScopePrice(entry.row) }}
                            </td>
                            <td
                                v-for="milestone in milestones"
                                :key="`group-${entry.row.id}-${milestone.id}`"
                                class="text-center"
                            >
                                <div class="d-flex justify-center align-center">
                                    <span>
                                        {{
                                            formatGroupMilestoneCell(
                                                entry.row.activeMembers,
                                                milestone.id
                                            )
                                        }}
                                    </span>
                                    <v-btn
                                        x-small
                                        icon
                                        class="ml-1"
                                        :disabled="
                                            !(
                                                entry.row.members &&
                                                entry.row.members.length
                                            )
                                        "
                                        @click="
                                            openBulkMilestoneDialog(
                                                entry.row,
                                                milestone.id
                                            )
                                        "
                                    >
                                        <v-icon>mdi-plus</v-icon>
                                    </v-btn>
                                </div>
                            </td>
                            <td class="text-center">
                                {{
                                    formatGroupProgress(entry.row.activeMembers)
                                }}
                            </td>
                            <td class="text-center">
                                <v-menu offset-y left>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            small
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon>mdi-dots-vertical</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list dense min-width="220">
                                        <v-subheader class="scope-menu-header">
                                            {{ entry.row.name }}
                                        </v-subheader>
                                        <v-list-item
                                            @click="openEditScope(entry.row)"
                                        >
                                            <v-list-item-icon class="mr-3">
                                                <v-icon small
                                                    >mdi-pencil</v-icon
                                                >
                                            </v-list-item-icon>
                                            <v-list-item-title
                                                >Edit scope
                                                group</v-list-item-title
                                            >
                                        </v-list-item>
                                        <v-list-item
                                            :disabled="
                                                !(
                                                    entry.row.costKeys &&
                                                    entry.row.costKeys.length
                                                )
                                            "
                                            @click="
                                                confirmReleaseAllItems(
                                                    entry.row
                                                )
                                            "
                                        >
                                            <v-list-item-icon class="mr-3">
                                                <v-icon small
                                                    >mdi-undo-variant</v-icon
                                                >
                                            </v-list-item-icon>
                                            <v-list-item-title
                                                >Release all
                                                items</v-list-item-title
                                            >
                                        </v-list-item>
                                        <v-list-item
                                            @click="
                                                confirmDeleteScope(entry.row)
                                            "
                                        >
                                            <v-list-item-icon class="mr-3">
                                                <v-icon small color="error"
                                                    >mdi-delete-outline</v-icon
                                                >
                                            </v-list-item-icon>
                                            <v-list-item-title
                                                class="error--text"
                                                >Delete scope
                                                group</v-list-item-title
                                            >
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </template>
                        <template v-else>
                            <td
                                class="text-center pl-6"
                                :class="{
                                    'member-excluded': entry.excluded,
                                }"
                            >
                                {{ entry.row.quoteNumber }}
                            </td>
                            <td
                                class="pl-10"
                                :class="{
                                    'member-excluded': entry.excluded,
                                }"
                            >
                                <div class="d-flex align-center">
                                    <span>{{ entry.row.reference }}</span>
                                    <v-chip
                                        v-if="entry.excluded"
                                        x-small
                                        class="ml-2"
                                        color="grey lighten-2"
                                    >
                                        Excluded
                                    </v-chip>
                                </div>
                            </td>
                            <td></td>
                            <td
                                class="text-right"
                                :class="{
                                    'member-excluded': entry.excluded,
                                }"
                            >
                                {{ formatScopePrice(entry.row) }}
                            </td>
                            <td
                                v-for="milestone in milestones"
                                :key="`${rowKey(entry.row)}-${milestone.id}`"
                                class="text-center"
                                :class="{
                                    'member-excluded': entry.excluded,
                                }"
                            >
                                <div class="d-flex justify-center align-center">
                                    <div
                                        v-if="
                                            entry.row.formatMilestones &&
                                                entry.row.formatMilestones[
                                                    milestone.id
                                                ]
                                        "
                                        class="milestone-cell-progress"
                                        :class="
                                            milestoneDateClass(
                                                entry.row,
                                                milestone.id
                                            )
                                        "
                                    >
                                        <div>
                                            {{
                                                formatDate(
                                                    entry.row.formatMilestones[
                                                        milestone.id
                                                    ].date._seconds ||
                                                        entry.row
                                                            .formatMilestones[
                                                            milestone.id
                                                        ].date.seconds
                                                )
                                            }}
                                        </div>
                                        <div>
                                            ({{
                                                Number(
                                                    entry.row.formatMilestones[
                                                        milestone.id
                                                    ].percentage || 0
                                                )
                                            }}%)
                                        </div>
                                    </div>
                                    <v-btn
                                        x-small
                                        icon
                                        class="ml-1"
                                        @click="
                                            openMilestoneDialog(
                                                entry.row,
                                                milestone.id
                                            )
                                        "
                                    >
                                        <v-icon>
                                            {{
                                                milestoneIcon(
                                                    entry.row,
                                                    milestone.id
                                                )
                                            }}
                                        </v-icon>
                                    </v-btn>
                                </div>
                            </td>
                            <td
                                class="text-center"
                                :class="{
                                    'member-excluded': entry.excluded,
                                }"
                            >
                                {{ formatMemberProgress(entry.row) }}
                            </td>
                            <td class="text-center">
                                <v-menu offset-y left>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            small
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon>mdi-dots-vertical</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list dense min-width="240">
                                        <v-subheader class="scope-menu-header">
                                            {{ entry.row.reference }}
                                        </v-subheader>
                                        <v-list-item
                                            @click="
                                                removeMemberFromGroup(
                                                    entry.groupId,
                                                    entry.row
                                                )
                                            "
                                        >
                                            <v-list-item-icon class="mr-3">
                                                <v-icon small
                                                    >mdi-link-off</v-icon
                                                >
                                            </v-list-item-icon>
                                            <v-list-item-title
                                                >Remove from
                                                group</v-list-item-title
                                            >
                                        </v-list-item>
                                        <v-list-item
                                            @click="
                                                toggleMemberExcluded(
                                                    entry.groupId,
                                                    entry.row
                                                )
                                            "
                                        >
                                            <v-list-item-icon class="mr-3">
                                                <v-icon small>
                                                    {{
                                                        entry.excluded
                                                            ? 'mdi-calculator'
                                                            : 'mdi-calculator-variant-outline'
                                                    }}
                                                </v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title>
                                                {{
                                                    entry.excluded
                                                        ? 'Include in calculations'
                                                        : 'Exclude from calculations'
                                                }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </template>
                    </tr>
                </tbody>
            </template>
        </v-data-table>

        <!-- CREATE / EDIT SCOPE -->
        <v-dialog
            v-model="scopeDialog"
            persistent
            max-width="720px"
            :retain-focus="false"
        >
            <CreateScopeDialog
                v-if="scopeDialog"
                :value="scopeDialog"
                :is-edit="!!editingScope"
                :scope="editingScope"
                :costs="costs"
                :product-types="productTypes"
                :assigned-key-map="assignedKeyMap"
                :currency="globalCurrency || 'COP'"
                :saving="scopeSaving"
                :format-currency="formatCurrency"
                :preselected-keys="preselectedKeys"
                @close="closeScopeDialog"
                @save="saveScope"
            />
        </v-dialog>

        <!-- REVIEW PENDING -->
        <v-dialog
            v-model="reviewDialog"
            persistent
            max-width="640px"
            :retain-focus="false"
        >
            <v-card v-if="reviewDialog">
                <v-card-title class="d-flex align-center">
                    <v-btn
                        icon
                        class="mr-2"
                        color="primary"
                        @click="closeReviewDialog"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <span>Pending items ({{ pendingItems.length }})</span>
                </v-card-title>
                <v-divider class="mx-4" />
                <v-card-text class="pt-4">
                    <div class="items-panel">
                        <div class="items-panel__search">
                            <v-text-field
                                v-model="reviewSearch"
                                placeholder="Filter by name or quote"
                                prepend-inner-icon="mdi-magnify"
                                clearable
                                hide-details
                                dense
                                solo
                                flat
                                background-color="transparent"
                                class="items-panel__search-field"
                            />
                        </div>
                        <div class="items-panel__list">
                            <div
                                v-for="item in filteredPendingItems"
                                :key="rowKey(item)"
                                class="items-panel__row items-panel__row--readonly"
                            >
                                <div class="items-panel__row-main">
                                    <span class="items-panel__quote">{{
                                        item.quoteNumber
                                    }}</span>
                                    <span class="items-panel__name">{{
                                        item.reference
                                    }}</span>
                                </div>
                                <span class="items-panel__price">
                                    {{ formatScopePrice(item) }}
                                </span>
                            </div>
                            <div
                                v-if="filteredPendingItems.length === 0"
                                class="items-panel__empty grey--text"
                            >
                                No items to show
                            </div>
                        </div>
                        <div class="items-panel__footer">
                            <span
                                >{{ filteredPendingItems.length }} of
                                {{ pendingItems.length }} items</span
                            >
                            <span>{{ pendingItems.length }} pending</span>
                        </div>
                    </div>
                </v-card-text>
                <v-divider class="mx-4" />
                <v-card-actions class="px-4 pb-4">
                    <v-spacer />
                    <v-btn text color="primary" @click="closeReviewDialog">
                        Close
                    </v-btn>
                    <v-btn color="primary" @click="createFromReview">
                        Create scope group
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- RELEASE ALL ITEMS -->
        <v-dialog
            v-model="releaseDialog"
            persistent
            max-width="480px"
            :retain-focus="false"
        >
            <v-card v-if="releaseDialog">
                <v-card-title class="d-flex align-center">
                    <v-btn
                        icon
                        class="mr-2"
                        color="primary"
                        @click="closeReleaseDialog"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <span>Release all items</span>
                </v-card-title>
                <v-divider class="mx-4" />
                <v-card-text class="pt-4">
                    <p class="mb-3">
                        The
                        <strong>{{ releaseItemsCount }}</strong>
                        scope↔item links of
                        <strong>{{
                            scopeToRelease && scopeToRelease.name
                        }}</strong>
                        will be removed, but the scope itself is kept, empty.
                    </p>
                    <ul class="pl-4 mb-0 grey--text text--darken-1">
                        <li>No item or quote is deleted.</li>
                        <li>
                            Milestone progress on each item is cleared.
                        </li>
                        <li>
                            The {{ releaseItemsCount }} items are counted as
                            pending again.
                        </li>
                    </ul>
                </v-card-text>
                <v-divider class="mx-4" />
                <v-card-actions class="px-4 pb-4">
                    <v-spacer />
                    <v-btn text color="primary" @click="closeReleaseDialog">
                        Cancel
                    </v-btn>
                    <v-btn
                        color="primary"
                        :loading="scopeSaving"
                        @click="releaseAllItems"
                    >
                        Release
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- DELETE SCOPE -->
        <v-dialog
            v-model="deleteDialog"
            persistent
            max-width="480px"
            :retain-focus="false"
        >
            <v-card v-if="deleteDialog">
                <v-card-title class="d-flex align-center">
                    <v-btn
                        icon
                        class="mr-2"
                        color="primary"
                        @click="closeDeleteDialog"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <span>Delete scope</span>
                </v-card-title>
                <v-divider class="mx-4" />
                <v-card-text class="pt-4">
                    <p class="mb-3">
                        Delete the scope
                        <strong>{{
                            scopeToDelete && scopeToDelete.name
                        }}</strong
                        >?
                    </p>
                    <ul class="pl-4 mb-0 grey--text text--darken-1">
                        <li>
                            Its
                            <strong>{{ deleteItemsCount }}</strong> item(s) are
                            counted as pending again.
                        </li>
                        <li>No item or quote is deleted.</li>
                        <li>
                            Milestone progress on each item is cleared.
                        </li>
                    </ul>
                </v-card-text>
                <v-divider class="mx-4" />
                <v-card-actions class="px-4 pb-4">
                    <v-spacer />
                    <v-btn text color="primary" @click="closeDeleteDialog">
                        Cancel
                    </v-btn>
                    <v-btn
                        color="error"
                        :loading="scopeSaving"
                        @click="deleteScope"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- MILESTONE DETAILS -->
        <v-dialog
            v-model="milestoneDialog"
            :retain-focus="false"
            persistent
            width="1300px"
        >
            <MilestoneDetails
                v-if="milestoneDialog"
                :selectedMilestone="selectedMilestone"
                :originalCost="selectedCost"
                :milestones="milestones"
                :settingId="settingId"
                :users="users"
                @replaceMilestones="replaceMilestones"
                @close="closeMilestoneDialog"
            />
        </v-dialog>

        <!-- BULK MILESTONE -->
        <v-dialog
            v-model="bulkMilestoneDialog"
            :retain-focus="false"
            persistent
            max-width="960px"
        >
            <BulkMilestoneDialog
                v-if="bulkMilestoneDialog"
                :group="bulkGroup"
                :milestone-id="bulkMilestoneId"
                :milestones="milestones"
                :members="bulkGroupMembers"
                :setting-id="settingId"
                @applied="onBulkMilestoneApplied"
                @close="closeBulkMilestoneDialog"
            />
        </v-dialog>
    </v-container>
</template>

<script>
import { mapMutations } from 'vuex'
import moment from 'moment'
import _ from 'lodash'
import API from '@/services/api'
import { uuid } from 'uuidv4'

export default {
    name: 'QuoteMilestonesGroups',
    props: {
        project: Object,
        users: Array,
    },
    components: {
        MilestoneDetails: () =>
            import('@/components/Milestones/MilestoneDetails.vue'),
        CreateScopeDialog: () =>
            import('@/components/Milestones/CreateScopeDialog.vue'),
        BulkMilestoneDialog: () =>
            import('@/components/Milestones/BulkMilestoneDialog.vue'),
    },
    data: () => ({
        loading: false,
        milestones: [],
        headers: [],
        settingId: null,
        costs: [],
        costsRevision: 0,
        scopeGroups: [],
        productTypes: [],
        expandedGroups: {},
        expandMode: 'collapse',
        globalCurrency: null,
        selectedCost: {},
        selectedMilestone: null,
        milestoneDialog: false,
        bulkMilestoneDialog: false,
        bulkGroup: null,
        bulkMilestoneId: null,
        scopeDialog: false,
        reviewDialog: false,
        reviewSearch: '',
        releaseDialog: false,
        deleteDialog: false,
        editingScope: null,
        scopeToRelease: null,
        scopeToDelete: null,
        preselectedKeys: [],
        scopeSaving: false,
        insights: [
            {
                color: '#3498DB',
                icon: 'mdi-format-list-bulleted',
                title: 'ITEMS',
            },
            {
                color: '#ffa414',
                icon: 'mdi-flag-triangle',
                title: 'MILESTONES',
            },
            {
                color: '#20844c',
                icon: 'mdi-check',
                title: 'COMPLETED',
            },
            {
                color: '#F08845',
                icon: 'mdi-percent-outline',
                title: 'PERCENTAGE',
            },
        ],
    }),
    async mounted() {
        try {
            this.loading = true
            this.scopeGroups = this.project.scopeGroups
                ? _.cloneDeep(this.project.scopeGroups)
                : []

            const {
                data: { settings },
            } = await API.getSettings()
            const setting = settings.find(s => s.name.toLowerCase() == 'quotes')
            const indexExchanges = settings.findIndex(
                u => u.name === 'Exchange'
            )
            this.globalCurrency =
                settings[indexExchanges] &&
                settings[indexExchanges].globalCurrency

            if (setting) {
                this.settingId = setting.id
                this.milestones = setting.milestones || []
                this.productTypes =
                    setting.quoteProductTypes ||
                    (await API.getQuoteProductTypes(setting.id)) ||
                    []
            }

            this.buildHeaders()

            const quotes = []
            ;(this.project.quotes || []).forEach(q =>
                quotes.push({ id: q.split('/')[0], number: q.split('/')[1] })
            )
            const quotesPromise =
                this.project && this.project.id
                    ? API.getProjectQuotes(this.project.id).catch(() => [])
                    : Promise.resolve([])
            for (const quote of quotes) {
                const costs = await API.getCosts(quote.id)
                costs.forEach(c => {
                    c.quoteId = quote.id
                    c.quoteNumber = quote.number
                })
                this.costs.push(...costs)
            }
            const currencyByQuoteId = {}
            const projectQuotes = await quotesPromise
            ;(projectQuotes || []).forEach(quote => {
                if (quote && quote.id && quote.currency) {
                    currencyByQuoteId[quote.id] = String(quote.currency)
                        .trim()
                        .toUpperCase()
                }
            })
            const fallbackCurrency = this.globalCurrency || 'COP'
            this.costs.forEach(cost => {
                cost.currency =
                    currencyByQuoteId[cost.quoteId] || fallbackCurrency
            })
            this.costs = this.costs.filter(obj => obj.type === 'cost')
            this.costs.forEach(c => {
                if (isNaN(c.quoteNumber) || !c.quoteNumber) {
                    c.quoteNumber = '-'
                }
            })
            this.formatMilestones()
            this.pruneInvalidGroups()
        } catch (error) {
            this.setErrorItems({
                name: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    computed: {
        costByKey() {
            const map = {}
            this.costs.forEach(cost => {
                map[this.rowKey(cost)] = cost
            })
            return map
        },
        assignedKeyMap() {
            const map = {}
            ;(this.scopeGroups || []).forEach(group => {
                ;(group.costKeys || []).forEach(key => {
                    map[key] = group.id
                })
            })
            return map
        },
        pendingItems() {
            return this.costs.filter(
                cost => !this.assignedKeyMap[this.rowKey(cost)]
            )
        },
        filteredPendingItems() {
            const term = (this.reviewSearch || '').toLowerCase().trim()
            if (!term) {
                return this.pendingItems
            }
            return this.pendingItems.filter(item => {
                const haystack = `${item.quoteNumber || ''} ${item.reference ||
                    ''}`.toLowerCase()
                return haystack.includes(term)
            })
        },
        releaseItemsCount() {
            if (!this.scopeToRelease) {
                return 0
            }
            return (this.scopeToRelease.costKeys || []).length
        },
        deleteItemsCount() {
            if (!this.scopeToDelete) {
                return 0
            }
            return (this.scopeToDelete.costKeys || []).length
        },
        bulkGroupMembers() {
            if (!this.bulkGroup) {
                return []
            }
            return this.bulkGroup.members || []
        },
        assignedCosts() {
            return this.costs.filter(
                cost => !!this.assignedKeyMap[this.rowKey(cost)]
            )
        },
        tableRows() {
            // Depend on costsRevision so Progress / insights refresh after milestone edits
            void this.costsRevision
            return (this.scopeGroups || []).map(group =>
                this.buildGroupRow(group)
            )
        },
        displayRows() {
            const rows = []
            this.tableRows.forEach(group => {
                rows.push({
                    kind: 'group',
                    key: `group-${group.id}`,
                    row: group,
                })
                if (this.expandedGroups[group.id]) {
                    ;(group.members || []).forEach(member => {
                        const key = this.rowKey(member)
                        rows.push({
                            kind: 'member',
                            key: `member-${key}`,
                            groupId: group.id,
                            excluded: !!(
                                group.excludedCostKeys &&
                                group.excludedCostKeys.includes(key)
                            ),
                            row: member,
                        })
                    })
                }
            })
            return rows
        },
        insightCards() {
            const info = this.milestonesInfo()
            return [
                {
                    ...this.insights[0],
                    value: this.scopeGroups.length,
                },
                {
                    ...this.insights[1],
                    value: info.active,
                },
                {
                    ...this.insights[2],
                    value: info.completed,
                },
                {
                    ...this.insights[3],
                    value: `${info.percentage.toFixed(2)} %`,
                },
            ]
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        rowKey(item) {
            return `${item.quoteId}/${item.id}`
        },
        buildHeaders() {
            const headers = [
                {
                    text: 'QUOTE',
                    value: 'quoteNumber',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'SCOPE / ITEM',
                    value: 'name',
                    sortable: false,
                    align: 'left',
                },
                {
                    text: 'TYPE OF SCOPE',
                    value: 'productTypeName',
                    sortable: false,
                    align: 'left',
                },
                {
                    text: 'PRICE',
                    value: 'salePrice',
                    sortable: false,
                    align: 'right',
                },
            ]
            ;(this.milestones || []).forEach(milestone => {
                headers.push({
                    text: (milestone.name || '').toUpperCase(),
                    value: 'milestone',
                    id: milestone.id,
                    weight: milestone.weight,
                    sortable: false,
                    align: 'center',
                })
            })
            headers.push({
                text: 'PROGRESS',
                value: 'progress',
                sortable: false,
                align: 'center',
            })
            headers.push({
                text: '',
                value: 'actions',
                sortable: false,
                align: 'center',
            })
            this.headers = headers
        },
        formatWeight(weight) {
            if (weight === null || weight === undefined || weight === '') {
                return '0'
            }
            return Number(Number(weight).toFixed(2))
        },
        pruneInvalidGroups() {
            const validKeys = new Set(this.costs.map(c => this.rowKey(c)))
            this.scopeGroups = (this.scopeGroups || []).map(group => {
                const costKeys = (group.costKeys || []).filter(key =>
                    validKeys.has(key)
                )
                const costKeySet = new Set(costKeys)
                return {
                    ...group,
                    costKeys,
                    excludedCostKeys: (
                        group.excludedCostKeys || []
                    ).filter(key => costKeySet.has(key)),
                }
            })
            if (this.project) {
                this.$set(this.project, 'scopeGroups', this.scopeGroups)
            }
        },
        buildGroupRow(group) {
            const excludedSet = new Set(group.excludedCostKeys || [])
            const members = (group.costKeys || [])
                .map(key => this.costByKey[key])
                .filter(Boolean)
            const activeMembers = members.filter(
                member => !excludedSet.has(this.rowKey(member))
            )
            const quoteNumbers = []
            members.forEach(member => {
                if (
                    member.quoteNumber &&
                    !quoteNumbers.includes(member.quoteNumber)
                ) {
                    quoteNumbers.push(member.quoteNumber)
                }
            })
            // Excluded items stay in the scope price: they are still part of
            // the budget even when they are left out of progress tracking.
            const fallbackCurrency = this.globalCurrency || 'COP'
            const totalsByCurrency = {}
            members.forEach(member => {
                const code = member.currency || fallbackCurrency
                totalsByCurrency[code] =
                    (totalsByCurrency[code] || 0) +
                    Number(member.salePrice || 0)
            })
            const priceByCurrency = Object.keys(totalsByCurrency).map(
                currency => ({
                    currency,
                    amount: totalsByCurrency[currency],
                })
            )
            const currency =
                priceByCurrency.length === 1
                    ? priceByCurrency[0].currency
                    : null
            const salePrice = members.reduce(
                (total, member) => total + Number(member.salePrice || 0),
                0
            )
            return {
                ...group,
                excludedCostKeys: [...(group.excludedCostKeys || [])],
                members,
                activeMembers,
                quoteNumbers: quoteNumbers.join(' - ') || '-',
                currency,
                priceByCurrency,
                salePrice,
            }
        },
        toggleExpand(groupId) {
            this.$set(
                this.expandedGroups,
                groupId,
                !this.expandedGroups[groupId]
            )
        },
        expandAll() {
            const next = {}
            this.scopeGroups.forEach(group => {
                next[group.id] = true
            })
            this.expandedGroups = next
            this.expandMode = 'expand'
        },
        collapseAll() {
            this.expandedGroups = {}
            this.expandMode = 'collapse'
        },
        getGroupMilestoneAverage(members, milestoneId) {
            if (!members || members.length === 0) {
                return null
            }
            const hasAny = members.some(
                member =>
                    member.formatMilestones &&
                    member.formatMilestones[milestoneId]
            )
            if (!hasAny) {
                return null
            }
            const total = members.reduce((sum, member) => {
                const milestone =
                    member.formatMilestones &&
                    member.formatMilestones[milestoneId]
                return sum + Number((milestone && milestone.percentage) || 0)
            }, 0)
            return total / members.length
        },
        formatGroupMilestoneCell(members, milestoneId) {
            const avg = this.getGroupMilestoneAverage(members, milestoneId)
            if (avg === null) {
                return ''
            }
            return `${avg.toFixed(1)}%`
        },
        formatGroupProgress(members) {
            if (!members || members.length === 0) {
                return '-'
            }
            const hasAny = members.some(
                m => m.milestones && m.milestones.length > 0
            )
            if (!hasAny) {
                return '-'
            }
            return `${this.getGroupProgressValue(members).toFixed(2)}%`
        },
        formatMemberProgress(member) {
            if (!member.milestones || member.milestones.length === 0) {
                return '-'
            }
            return `${this.getScopeProgress(member).toFixed(2)}%`
        },
        openCreateScope() {
            this.editingScope = null
            this.preselectedKeys = []
            this.scopeDialog = true
        },
        openEditScope(row) {
            this.editingScope = {
                id: row.id,
                name: row.name,
                productTypeId: row.productTypeId,
                productTypeName: row.productTypeName,
                costKeys: [...(row.costKeys || [])],
            }
            this.preselectedKeys = []
            this.scopeDialog = true
        },
        closeScopeDialog() {
            this.scopeDialog = false
            this.editingScope = null
            this.preselectedKeys = []
        },
        openReviewDialog() {
            this.reviewSearch = ''
            this.reviewDialog = true
        },
        closeReviewDialog() {
            this.reviewDialog = false
            this.reviewSearch = ''
        },
        createFromReview() {
            this.closeReviewDialog()
            this.editingScope = null
            this.preselectedKeys = this.pendingItems.map(item =>
                this.rowKey(item)
            )
            this.scopeDialog = true
        },
        confirmReleaseAllItems(row) {
            this.scopeToRelease = row
            this.releaseDialog = true
        },
        closeReleaseDialog() {
            this.releaseDialog = false
            this.scopeToRelease = null
        },
        async releaseAllItems() {
            if (!this.scopeToRelease) {
                return
            }
            try {
                const removedKeys = [...(this.scopeToRelease.costKeys || [])]
                const nextGroups = this.scopeGroups.map(group =>
                    group.id === this.scopeToRelease.id
                        ? { ...group, costKeys: [], excludedCostKeys: [] }
                        : group
                )
                await this.persistScopeGroups(nextGroups)
                await this.clearMilestonesForCostKeys(removedKeys)
                this.closeReleaseDialog()
            } catch (error) {
                this.setErrorItems({
                    name: this.$options.name,
                    message: error.message,
                })
            }
        },
        async removeMemberFromGroup(groupId, member) {
            const costKey = this.rowKey(member)
            try {
                const nextGroups = this.scopeGroups.map(group => {
                    if (group.id !== groupId) {
                        return group
                    }
                    return {
                        ...group,
                        costKeys: (group.costKeys || []).filter(
                            key => key !== costKey
                        ),
                        excludedCostKeys: (group.excludedCostKeys || []).filter(
                            key => key !== costKey
                        ),
                    }
                })
                await this.persistScopeGroups(nextGroups)
                await this.clearMilestonesForCostKeys([costKey])
            } catch (error) {
                this.setErrorItems({
                    name: this.$options.name,
                    message: error.message,
                })
            }
        },
        async toggleMemberExcluded(groupId, member) {
            const costKey = this.rowKey(member)
            try {
                const nextGroups = this.scopeGroups.map(group => {
                    if (group.id !== groupId) {
                        return group
                    }
                    const excluded = new Set(group.excludedCostKeys || [])
                    if (excluded.has(costKey)) {
                        excluded.delete(costKey)
                    } else {
                        excluded.add(costKey)
                    }
                    return {
                        ...group,
                        excludedCostKeys: [...excluded],
                    }
                })
                await this.persistScopeGroups(nextGroups)
            } catch (error) {
                this.setErrorItems({
                    name: this.$options.name,
                    message: error.message,
                })
            }
        },
        confirmDeleteScope(row) {
            this.scopeToDelete = row
            this.deleteDialog = true
        },
        closeDeleteDialog() {
            this.deleteDialog = false
            this.scopeToDelete = null
        },
        async deleteScope() {
            if (!this.scopeToDelete) {
                return
            }
            try {
                const scopeId = this.scopeToDelete.id
                const sourceGroup = (this.scopeGroups || []).find(
                    group => group.id === scopeId
                )
                const removedKeys = [
                    ...((sourceGroup && sourceGroup.costKeys) ||
                        this.scopeToDelete.costKeys ||
                        []),
                ]
                const nextGroups = this.scopeGroups.filter(
                    group => group.id !== scopeId
                )
                await this.persistScopeGroups(nextGroups)
                await this.clearMilestonesForCostKeys(removedKeys)
                if (this.expandedGroups[scopeId]) {
                    this.$delete(this.expandedGroups, scopeId)
                }
                this.closeDeleteDialog()
            } catch (error) {
                this.setErrorItems({
                    name: this.$options.name,
                    message: error.message,
                })
            }
        },
        async clearMilestonesForCostKeys(costKeys) {
            const keys = [...new Set(costKeys || [])].filter(Boolean)
            for (const key of keys) {
                const [quoteId, costId] = String(key).split('/')
                if (!quoteId || !costId) {
                    continue
                }
                await API.clearCostMilestones({ quoteId, costId })
                const costIndex = this.costs.findIndex(
                    c => c.id == costId && c.quoteId == quoteId
                )
                if (costIndex > -1) {
                    this.$set(this.costs[costIndex], 'milestones', [])
                    this.$set(this.costs[costIndex], 'formatMilestones', {})
                }
            }
            if (keys.length > 0) {
                this.formatMilestones()
                this.touchCosts()
            }
        },
        async persistScopeGroups(groups) {
            this.scopeSaving = true
            try {
                const cleaned = (groups || []).map(group => {
                    const costKeys = [...(group.costKeys || [])]
                    const costKeySet = new Set(costKeys)
                    return {
                        id: group.id,
                        name: group.name,
                        productTypeId: group.productTypeId,
                        productTypeName: group.productTypeName,
                        costKeys,
                        excludedCostKeys: (
                            group.excludedCostKeys || []
                        ).filter(key => costKeySet.has(key)),
                    }
                })
                const saved = await API.updateScopeGroups(
                    this.project.id,
                    cleaned
                )
                this.scopeGroups = saved || cleaned
                if (this.project) {
                    this.$set(this.project, 'scopeGroups', this.scopeGroups)
                }
            } finally {
                this.scopeSaving = false
            }
        },
        async saveScope(payload) {
            try {
                let nextGroups
                let removedKeys = []
                const costKeySet = new Set(payload.costKeys || [])
                if (payload.id) {
                    const previous = this.scopeGroups.find(
                        group => group.id === payload.id
                    )
                    removedKeys = (previous && previous.costKeys
                        ? previous.costKeys
                        : []
                    ).filter(key => !costKeySet.has(key))
                    nextGroups = this.scopeGroups.map(group =>
                        group.id === payload.id
                            ? {
                                  ...group,
                                  name: payload.name,
                                  productTypeId: payload.productTypeId,
                                  productTypeName: payload.productTypeName,
                                  costKeys: payload.costKeys,
                                  excludedCostKeys: (
                                      group.excludedCostKeys || []
                                  ).filter(key => costKeySet.has(key)),
                              }
                            : group
                    )
                } else {
                    nextGroups = [
                        ...this.scopeGroups,
                        {
                            id: uuid().substring(0, 8),
                            name: payload.name,
                            productTypeId: payload.productTypeId,
                            productTypeName: payload.productTypeName,
                            costKeys: payload.costKeys,
                            excludedCostKeys: [],
                        },
                    ]
                }
                await this.persistScopeGroups(nextGroups)
                if (removedKeys.length > 0) {
                    await this.clearMilestonesForCostKeys(removedKeys)
                }
                this.closeScopeDialog()
            } catch (error) {
                this.setErrorItems({
                    name: this.$options.name,
                    message: error.message,
                })
            }
        },
        formatDate(seconds) {
            return `${moment.unix(seconds).format('YYYY/MM/DD')}`
        },
        dateDiff(date) {
            const seconds = date._seconds || date.seconds
            return moment.unix(seconds).diff(moment(), 'days')
        },
        milestoneDateClass(item, milestoneId) {
            const milestone =
                item.formatMilestones && item.formatMilestones[milestoneId]
            if (!milestone) {
                return {}
            }
            return {
                'text-gray':
                    this.dateDiff(milestone.date) > 20 &&
                    milestone.percentage < 100,
                'text-orange':
                    this.dateDiff(milestone.date) > 6 &&
                    this.dateDiff(milestone.date) < 20 &&
                    milestone.percentage < 100,
                'text-red':
                    this.dateDiff(milestone.date) < 7 &&
                    milestone.percentage < 100,
                'text-green': milestone.percentage == 100,
            }
        },
        milestoneIcon(item, milestoneId) {
            const milestone =
                item.formatMilestones && item.formatMilestones[milestoneId]
            if (!milestone) {
                return 'mdi-plus'
            }
            return milestone.percentage == 100 ? 'mdi-eye' : 'mdi-pencil'
        },
        openMilestoneDialog(item, milestoneId) {
            this.selectedCost = _.cloneDeep(item)
            this.selectedMilestone = milestoneId
            this.milestoneDialog = true
        },
        closeMilestoneDialog() {
            this.selectedCost = {}
            this.selectedMilestone = null
            this.milestoneDialog = false
        },
        openBulkMilestoneDialog(group, milestoneId) {
            if (!group || !(group.members && group.members.length)) {
                return
            }
            this.bulkGroup = group
            this.bulkMilestoneId = milestoneId
            this.bulkMilestoneDialog = true
        },
        closeBulkMilestoneDialog() {
            this.bulkMilestoneDialog = false
            this.bulkGroup = null
            this.bulkMilestoneId = null
        },
        onBulkMilestoneApplied(results) {
            ;(results || []).forEach(({ costId, milestones }) => {
                const costIndex = this.costs.findIndex(c => c.id == costId)
                if (costIndex > -1) {
                    this.$set(this.costs[costIndex], 'milestones', milestones)
                }
            })
            this.formatMilestones()
            this.touchCosts()
            this.closeBulkMilestoneDialog()
        },
        replaceMilestones({ costId, milestones }) {
            const costIndex = this.costs.findIndex(c => c.id == costId)
            if (costIndex > -1) {
                this.$set(this.costs[costIndex], 'milestones', milestones)
                this.formatMilestones()
                this.touchCosts()
            }
        },
        touchCosts() {
            this.costsRevision += 1
        },
        formatMilestones() {
            this.costs.forEach(cost => {
                const formatMilestones = {}
                if (cost.milestones) {
                    cost.milestones.forEach(milestone => {
                        if (typeof milestone.date == 'string') {
                            const date = new Date(milestone.date)
                            const _seconds = Math.floor(date.getTime() / 1000)
                            milestone.date = { _seconds }
                        }
                        formatMilestones[milestone.id] = milestone
                    })
                }
                this.$set(cost, 'formatMilestones', formatMilestones)
            })
        },
        formatScopePrice(row) {
            if (!row) {
                return '-'
            }
            if (row.priceByCurrency && row.priceByCurrency.length > 1) {
                const parts = row.priceByCurrency
                    .map(entry =>
                        this.formatCurrency(entry.currency, entry.amount)
                    )
                    .filter(text => text && text !== '-')
                return parts.length ? parts.join(' · ') : '-'
            }
            const currency =
                row.currency ||
                (row.priceByCurrency &&
                    row.priceByCurrency[0] &&
                    row.priceByCurrency[0].currency) ||
                this.globalCurrency ||
                'COP'
            return this.formatCurrency(currency, row.salePrice)
        },
        formatCurrency(currency, value) {
            const code = String(currency || '')
                .trim()
                .toUpperCase()
            if (!code) {
                return '-'
            }
            if (!value) {
                return '-'
            }
            try {
                const formattedValue = new Intl.NumberFormat('de-DE', {
                    style: 'currency',
                    currency: code,
                    maximumFractionDigits: 0,
                }).format(+value)
                if (Math.abs((+value || 0).toFixed(0)) == 0) {
                    return '-'
                }
                return formattedValue
            } catch (error) {
                return '-'
            }
        },
        getScopeValue(cost) {
            return Number(cost.salePrice2 || cost.salePrice || 0)
        },
        getMilestoneWeight(milestoneId) {
            const definition = (this.milestones || []).find(
                m => m.id == milestoneId
            )
            return Number((definition && definition.weight) || 0)
        },
        getScopeProgress(cost) {
            const definitions = this.milestones || []
            if (!definitions.length) {
                return 0
            }
            const progressById = {}
            ;(cost.milestones || []).forEach(milestone => {
                progressById[milestone.id] = Number(milestone.percentage || 0)
            })
            // Each milestone weight is its share of the overall 100% progress.
            return definitions.reduce((total, definition) => {
                const percentage = progressById[definition.id] || 0
                const weight = Number(definition.weight || 0)
                return total + (percentage * weight) / 100
            }, 0)
        },
        getGroupProgressValue(members) {
            if (!members || members.length === 0) {
                return 0
            }
            const hasAny = members.some(
                m => m.milestones && m.milestones.length > 0
            )
            if (!hasAny) {
                return 0
            }
            const total = members.reduce(
                (sum, member) => sum + this.getScopeProgress(member),
                0
            )
            return total / members.length
        },
        milestonesInfo() {
            let milestonesInfo = { active: 0, completed: 0, percentage: 0 }
            const costs = this.assignedCosts

            costs.forEach(c => {
                if (c.milestones) {
                    milestonesInfo.active += c.milestones.length
                    milestonesInfo.completed += c.milestones.reduce(
                        (accumulator, milestone) =>
                            accumulator + (milestone.percentage == 100 ? 1 : 0),
                        0
                    )
                }
            })

            // PERCENTAGE = Σ(group progress × tracked price) / Σ(tracked prices)
            // Excluded items stay in the displayed scope price, but they are
            // not followed, so they do not weight the progress percentage.
            let weightedProgress = 0
            let totalGroupPrice = 0
            ;(this.tableRows || []).forEach(group => {
                const groupPrice = (group.activeMembers || []).reduce(
                    (total, member) => total + Number(member.salePrice || 0),
                    0
                )
                const groupProgress = this.getGroupProgressValue(
                    group.activeMembers
                )
                weightedProgress += groupProgress * groupPrice
                totalGroupPrice += groupPrice
            })

            if (totalGroupPrice > 0) {
                milestonesInfo.percentage = weightedProgress / totalGroupPrice
            }

            return milestonesInfo
        },
    },
}
</script>

<style>
.v-data-table {
    border: 1px solid #eeeeee;
}
.milestones-table-top {
    background-color: #eeeeee;
}
thead {
    background: #eeeeee;
}
.v-input .v-label {
    font-size: 12px;
}
.text-gray {
    color: rgb(95, 95, 95);
    font-weight: bold;
}
.text-orange {
    color: orange;
    font-weight: bold;
}
.text-green {
    color: green;
    font-weight: bold;
}
.text-red {
    color: red;
    font-weight: bold;
}
.group-row {
    background-color: #fafafa;
}
.group-member-row {
    background-color: #ffffff;
}
.member-excluded {
    opacity: 0.55;
}
.milestone-cell-progress {
    line-height: 1.2;
    font-size: 12px;
    font-weight: bold;
    text-align: center;
}
.scope-menu-header {
    font-size: 11px !important;
    font-weight: 700 !important;
    letter-spacing: 0.4px;
    height: 36px !important;
    padding-left: 16px !important;
    padding-right: 16px !important;
    text-transform: uppercase;
}
.items-panel {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
    background: #fff;
}
.items-panel__search {
    background: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
    padding: 2px 4px;
}
.items-panel__search-field .v-input__slot {
    min-height: 36px !important;
    box-shadow: none !important;
}
.items-panel__list {
    max-height: 280px;
    overflow-y: auto;
}
.items-panel__row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid #eeeeee;
}
.items-panel__row--readonly {
    cursor: default;
}
.items-panel__row-main {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 6px;
}
.items-panel__quote {
    color: #9e9e9e;
    font-size: 13px;
    font-weight: 500;
}
.items-panel__name {
    color: rgba(0, 0, 0, 0.87);
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
}
.items-panel__price {
    color: #9e9e9e;
    font-size: 13px;
    white-space: nowrap;
    margin-left: 8px;
}
.items-panel__empty {
    padding: 24px 12px;
    text-align: center;
}
.items-panel__footer {
    display: flex;
    justify-content: space-between;
    background: #f5f5f5;
    border-top: 1px solid #e0e0e0;
    padding: 8px 12px;
    font-size: 12px;
    color: #757575;
}
.empty-state {
    background: #fff;
    min-height: 320px;
}
.pending-alert {
    background-color: #fff4d1 !important;
    border: 1px solid #f5d98a !important;
    border-left: 4px solid #f0ad4e !important;
    box-shadow: none !important;
}
.pending-alert .v-alert__wrapper {
    align-items: center;
}
.pending-alert .v-alert__icon {
    color: #f0ad4e !important;
    margin-right: 8px;
    margin-top: 4px;
}
.pending-alert__text {
    color: #5c4a32;
    font-size: 16px;
}
.pending-alert__action {
    color: #d9890d !important;
    font-weight: 700 !important;
    letter-spacing: 0.6px;
    text-transform: uppercase !important;
}
.review-list {
    max-height: 360px;
    overflow-y: auto;
}
</style>
